import type {SubagentHandle} from '@github-ui/ahp-relay/ahp-session-selectors'
import type {ChatState} from '@microsoft/agent-host-protocol'
import {useState, type ComponentType} from 'react'
import {useAhpChatState} from '../../../../ahp/use-ahp-child-session-state'
import {useSessionContext} from '../../../../contexts/SessionContext'
import {SubagentSessionGroup} from '../../SubagentSessionGroup/SubagentSessionGroup'
import {LegacyChatInitialMessageContext} from '../../../task-view/legacy-chat-initial-message-context'

export interface AhpSubagentTimelineProps {
  state: ChatState | undefined
  view: 'ready' | 'error' | 'loading'
  errorMessage?: string
  additionalErrorMessage?: string
  ariaLabel: string
}

export interface AhpSubagentComponentProps {
  subagent: SubagentHandle
  offline?: boolean
}

export type AhpSubagentComponent = ComponentType<AhpSubagentComponentProps>

export function AhpSubagentReference({
  subagent,
  TimelineComponent,
  offline = false,
  persistedState,
  hasPersistedActions = false,
}: {
  subagent: SubagentHandle
  TimelineComponent: ComponentType<AhpSubagentTimelineProps>
  offline?: boolean
  persistedState?: ChatState
  hasPersistedActions?: boolean
}) {
  const {session} = useSessionContext()
  const [isExpanded, setIsExpanded] = useState(false)
  const {state, error} = useAhpChatState({
    taskId: session.task_id,
    channel: subagent.resource,
    enabled: isExpanded && !offline,
  })
  const renderedState = offline && !hasPersistedActions ? undefined : offline ? persistedState : state
  const renderedError = offline && !persistedState ? 'Persisted subagent history is unavailable.' : error

  return (
    <SubagentSessionGroup
      title={`Subagent: ${subagent.title}`}
      open={isExpanded}
      statusText={subagent.agentName}
      onOpenChange={setIsExpanded}
    >
      <LegacyChatInitialMessageContext value={undefined}>
        <TimelineComponent
          state={renderedState}
          view={renderedState ? 'ready' : renderedError ? 'error' : offline ? 'ready' : 'loading'}
          {...(renderedError ? {errorMessage: renderedError} : {})}
          {...(renderedState && renderedError ? {additionalErrorMessage: renderedError} : {})}
          ariaLabel={`${subagent.title} subagent timeline`}
        />
      </LegacyChatInitialMessageContext>
    </SubagentSessionGroup>
  )
}
